import React from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  IconButton,
  Link,
  Stack,
  type SxProps,
  type Theme,
  Tooltip,
  Typography,
} from '@mui/material';

/** Styles all required markers (MUI labels and custom FieldLabel) consistently in red. */
export const PAYLOAD_BUILDER_REQUIRED_FIELDS_SX = {
  '& .MuiFormLabel-asterisk': {
    color: 'error.main',
  },
} as const;

/** Matches MUI InputLabel required indicator markup so styling stays consistent. */
export function RequiredMark() {
  return <span className="MuiFormLabel-asterisk"> *</span>;
}

interface FieldLabelProps {
  children: React.ReactNode;
  required?: boolean;
  sx?: SxProps<Theme>;
}

export function FieldLabel({ children, required, sx }: FieldLabelProps) {
  return (
    <Typography variant="body2" fontWeight={500} sx={{ mb: 1, ...sx }}>
      {children}
      {required ? <RequiredMark /> : null}
    </Typography>
  );
}

interface FieldLabelWithLinkProps {
  label: string;
  required?: boolean;
  href?: string;
  htmlFor?: string;
  infoText?: string;
}

export function FieldLabelWithLink({ label, required, href, htmlFor, infoText }: FieldLabelWithLinkProps) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
      <Typography component="label" htmlFor={htmlFor} variant="body2" fontWeight={500}>
        {label}
        {required ? <RequiredMark /> : null}
      </Typography>
      {href ? (
        <IconButton
          component={Link}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          aria-label={`Open ${label}`}
          sx={{ color: 'text.secondary', p: 0.25, '&:hover': { color: 'primary.main' } }}
        >
          <OpenInNewIcon sx={{ fontSize: 12 }} />
        </IconButton>
      ) : null}
      {infoText ? (
        <Tooltip title={infoText} placement="top" arrow>
          <InfoOutlinedIcon fontSize="small" color="action" sx={{ fontSize: 14 }} />
        </Tooltip>
      ) : null}
    </Stack>
  );
}
